import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { Viewer } from '../Viewer';
import { generateDefaultUserIcon } from '@framework/utils';
import { useFollowingUser } from '../useFollowingUser';

type Props = {
  viewer: Viewer;
  setClientViewBox: (viewBox: Rect) => void;
};

export const ViewerPopupContent: React.FC<Props> = ({ viewer, setClientViewBox }: Props) => {
  const { t } = useTranslation();
  const { startFollowing, stopFollowing, isFollowing } = useFollowingUser();
  const isFollowingThisViewer = isFollowing(viewer.userId);

  const handleClick = useCallback(() => {
    if (isFollowingThisViewer) {
      // フォロー解除
      stopFollowing();
    } else {
      // フォロー開始と同時にビューポートを即座にジャンプ
      startFollowing(viewer.userId);
      setClientViewBox(viewer.rect);
    }
  }, [isFollowingThisViewer, stopFollowing, startFollowing, viewer.userId, setClientViewBox, viewer.rect]);

  return (
    <div
      className="block cursor-pointer py-2 pl-3 text-gray-800 hover:bg-indigo-500 hover:text-white"
      onClick={handleClick}
    >
      <div className="flex items-center">
        <div
          className={`inline-block size-6 overflow-hidden rounded-full ${isFollowingThisViewer ? 'ring-2 ring-orange-500' : ''}`}
        >
          {viewer.imageUrl ? (
            <img className="size-full object-cover" src={viewer.imageUrl} alt={viewer.userName} />
          ) : (
            <div className="size-full object-cover">
              {generateDefaultUserIcon({
                id: viewer.userId,
                name: viewer.userName,
                isAnonymous: viewer.isAnonymous,
                size: 24,
              })}
            </div>
          )}
        </div>
        <div className="inline-block w-40 truncate px-2 text-sm leading-6">{viewer.userName}</div>
      </div>
      {isFollowingThisViewer && (
        <div className="pl-8 text-xs text-orange-500">{t('canvas.viewer.popupContent.unfollow')}</div>
      )}
    </div>
  );
};
